
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { getUserTeamIds } from "@/pages/guards/services/guardFetchService";
import { useUserRoles } from "@/hooks/useUserRoles";
import { TeamApplicationCard } from "./components/TeamApplicationCard";
import { useLanguage } from "@/contexts/LanguageContext";

const Applications = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin, roles } = useUserRoles();
  const { t } = useLanguage();

  const { data: teams, isLoading } = useQuery({
    queryKey: ['teams-with-applications', user?.id, isAdmin],
    queryFn: async () => {
      if (!user) return [];

      if (isAdmin) {
        const { data: teams, error } = await supabase
          .from('teams')
          .select(`
            *,
            applications(count)
          `)
          .eq('status', 'active')
          .order('name');

        if (error) {
          toast({
            title: t('errorLoadingTeams'),
            description: error.message,
            variant: "destructive",
          });
          return [];
        }

        return teams;
      }
      
      const userTeamIds = await getUserTeamIds(user.id);
      
      if (userTeamIds.length === 0) {
        return [];
      }
      
      const { data: teams, error } = await supabase
        .from('teams')
        .select(`
          *,
          applications(count)
        `)
        .in('id', userTeamIds)
        .eq('status', 'active')
        .order('name');

      if (error) {
        toast({
          title: t('errorLoadingTeams'),
          description: error.message,
          variant: "destructive",
        });
        return [];
      }

      return teams;
    },
    enabled: !!user,
  });

  // Check if user is a guard or client
  const canCreateApplication = !['guard', 'client'].includes(roles?.[0] || '');

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-8 animate-fadeIn">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-black dark:text-white">{t('workApplications')}</h1>
              <p className="text-black/70 dark:text-gray-300">{t('loadingTeams')}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-44" />
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-black dark:text-white">{t('workApplications')}</h1>
            <p className="text-black/70 dark:text-gray-300">{t('viewManageApplications')}</p>
          </div>
          {canCreateApplication && (
            <Button 
              className="flex items-center gap-2 bg-primary hover:bg-primary-600 text-white"
              onClick={() => {
                if (teams && teams.length > 0) {
                  navigate(`/applications/team/${teams[0].id}`);
                } else {
                  toast({
                    title: t('noTeamsAvailable'),
                    description: t('pleaseCreateTeamFirst'),
                    variant: "destructive",
                  });
                }
              }}
            >
              <Plus className="w-4 h-4" />
              {t('createNewApplication')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {teams && teams.length > 0 ? (
            teams.map((team) => (
              <TeamApplicationCard
                key={team.id}
                team={team}
                onClick={() => navigate(`/applications/team/${team.id}`)}
                canEdit={isAdmin}
                canDelete={false}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-muted-foreground dark:text-gray-400">
              {t('noTeamsFoundCreateFirst')}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Applications;
